import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Sparkles } from 'lucide-react';
import { PROMO, SPECIAL_PACKAGES, isPromoActive } from '@/lib/data';

/**
 * Fourth of July limited-time promo banner. Renders nothing outside the window.
 */
export const PromoBanner = ({ variant = 'strip' }) => {
  if (!isPromoActive()) return null;

  if (variant === 'strip') {
    return (
      <div
        data-testid="promo-strip-banner"
        className="relative overflow-hidden bg-gradient-to-r from-[#7a1620] via-[#0B0F1A] to-[#7a1620] border-b border-[#C9A24A]/30 z-40 pt-20"
      >
        <div className="luxe-container py-3 flex items-center justify-center gap-3 sm:gap-6 flex-wrap text-center">
          <span className="text-[10px] uppercase tracking-[0.32em] text-[#C9A24A] font-semibold flex items-center gap-2">
            <Sparkles size={12} /> 4th of July Special
          </span>
          <span className="text-white/85 text-xs sm:text-sm">
            Full Detail <span className="text-[#C9A24A] font-semibold">$70</span> · Interior Detail{' '}
            <span className="text-[#C9A24A] font-semibold">$40</span>
          </span>
          <span className="text-white/60 text-xs flex items-center gap-1.5">
            <Calendar size={12} className="text-[#C9A24A]" /> {PROMO.validFromLabel} – {PROMO.validToLabel}
          </span>
          <Link
            to="/booking"
            data-testid="promo-strip-book"
            className="text-[10px] uppercase tracking-[0.28em] text-[#C9A24A] hover:text-white transition-colors flex items-center gap-1.5 font-semibold"
          >
            Book <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    );
  }

  // "feature" variant — big section with flyer + cards
  return (
    <section
      data-testid="promo-feature-section"
      className="relative py-24 sm:py-28 overflow-hidden border-y border-[#C9A24A]/20"
      style={{
        background:
          'radial-gradient(ellipse at top left, rgba(178,34,52,0.28), transparent 55%), radial-gradient(ellipse at bottom right, rgba(11,25,58,0.6), transparent 60%), #0b0f1a',
      }}
    >
      <div className="grain-overlay" />
      <div className="luxe-container relative z-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#C9A24A]" />
            <span className="luxe-eyebrow text-[#C9A24A]">Limited Time Only</span>
          </div>
          <h2 className="text-4xl sm:text-6xl leading-[1.03] text-white font-serif">
            Fourth of July <br />
            <span className="italic text-[#C9A24A]">Special</span>.
          </h2>
          <p className="mt-6 text-white/70 max-w-lg leading-relaxed">
            {PROMO.subtitle} Two premium detailing tiers at holiday pricing —
            available for a limited window only.
          </p>

          <div className="mt-6 inline-flex items-center gap-3 px-4 py-3 border border-[#C9A24A]/40 bg-[#0B0F1A]">
            <Calendar size={16} className="text-[#C9A24A]" />
            <span className="text-white text-sm">
              Deal valid{' '}
              <span className="text-[#C9A24A] font-semibold tracking-wide">
                {PROMO.validFromLabel} – {PROMO.validToLabel}
              </span>
            </span>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {SPECIAL_PACKAGES.map((p) => (
              <div
                key={p.id}
                data-testid={p.testId}
                className="luxe-card p-6 border-[#C9A24A]/50"
              >
                <div className="luxe-eyebrow text-[#C9A24A] mb-2">
                  {p.displayName.replace(' Special', '')}
                </div>
                <div className="flex items-baseline gap-2 flex-wrap mb-3">
                  <span className="package-price text-5xl">${p.priceSedan}</span>
                  <span className="text-white/40 text-[10px] uppercase tracking-widest">
                    Sedan
                  </span>
                  <span className="text-white/30">/</span>
                  <span className="package-price text-3xl">${p.priceSuv}</span>
                  <span className="text-white/40 text-[10px] uppercase tracking-widest">
                    SUV/Truck
                  </span>
                </div>
                <ul className="space-y-1.5 mb-5">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="text-white/75 text-xs flex items-start gap-2 leading-snug"
                    >
                      <span className="text-[#C9A24A] mt-0.5">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/booking?package=${encodeURIComponent(p.name)}`}
                  data-testid={`${p.testId}-book`}
                  className="btn-book w-full !py-2.5 text-[10px]"
                >
                  Book This
                </Link>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -inset-3 border border-[#C9A24A]/40 pointer-events-none" />
            <img
              src={PROMO.flyer}
              alt="Fourth of July Special flyer"
              data-testid="promo-flyer-image"
              className="relative w-full h-auto object-cover shadow-[0_30px_80px_-30px_rgba(201,162,74,0.5)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
